import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  FlaskConical,
  FileCheck2,
  Network,
  ShieldCheck,
  HeartPulse,
  Activity,
  CheckCircle2,
  ListChecks,
  ShieldAlert,
  FolderArchive,
  Tag,
  ClipboardList,
  Truck,
  Loader2,
  Send,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { HERO, CHANGE_IMPACT, ABOUT, PLATFORM, USE_CASES, SECURITY, WHY_US, BOOK_DEMO_URL } from "../data/mock";
import {
  FadeIn,
  SplitText,
  BlurText,
  SpotlightCard,
  HexPattern,
  Eyebrow,
  Magnetic,
  Marquee,
  FloatingHexes,
} from "../components/reactbits/Anim";

const MARQUEE_ITEMS = [
  "Change Impact Analysis",
  "Cross-System Traceability",
  "Sustaining Engineering",
  "Regulatory Documentation",
  "Knowledge Graph",
  "510(k) Drafts",
  "HIPAA & GDPR Designed",
  "Unified Knowledge View",
];

const CI_ICONS = { ListChecks, ShieldAlert, FolderArchive, Tag, ClipboardList, Truck };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Compact 4-field lead form for the home page */
const HomeLeadForm = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fieldCls = "border-[#E5DCC9] bg-white text-[#1F1B13] placeholder:text-[#B8AE9C] focus-visible:ring-[#E8930C]/50";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        message: form.message.trim(),
        consent: true,
        interest: "Home lead form",
        website: form.website,
      });
      setSent(true);
    } catch {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-3xl border border-[#F0A322]/40 bg-[#F0A322]/[0.07] p-10 text-center" data-testid="home-lead-success">
        <CheckCircle2 className="mx-auto h-10 w-10 text-[#E8930C]" />
        <p className="mt-4 font-heading text-xl font-semibold text-[#1F1B13]">Thanks, your request has been sent.</p>
        <p className="mt-2 text-sm text-[#5D564A]">We read every enquiry and reply as quickly as we can.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="rounded-3xl border border-[#EDE5D3] bg-white p-8 shadow-[0_8px_40px_rgba(31,27,19,0.06)]" data-testid="home-lead-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Input data-testid="home-lead-name" value={form.name} onChange={set("name")} placeholder="Full name *" className={fieldCls} />
        <Input data-testid="home-lead-email" type="email" value={form.email} onChange={set("email")} placeholder="Work email *" className={fieldCls} />
      </div>
      <Input data-testid="home-lead-company" value={form.company} onChange={set("company")} placeholder="Company" className={`mt-5 ${fieldCls}`} />
      <Textarea data-testid="home-lead-message" rows={3} value={form.message} onChange={set("message")} placeholder="Tell us about your change scenario *" className={`mt-5 ${fieldCls}`} />
      {/* honeypot - hidden from humans */}
      <input type="text" value={form.website} onChange={set("website")} name="website" tabIndex="-1" autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} />
      <button
        type="submit"
        disabled={submitting}
        data-testid="home-lead-submit"
        className="group mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#F0A322] px-8 py-3.5 text-sm font-semibold uppercase tracking-wide text-[#231A07] transition-colors duration-200 hover:bg-[#E8930C] disabled:opacity-60 sm:w-auto"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        send message
      </button>
      <p className="mt-4 text-xs text-[#8A8272]">
        By submitting, you agree to the MedHive <Link to="/privacy-policy" className="text-[#B45309] underline-offset-2 hover:underline">Privacy Policy</Link>.
      </p>
    </form>
  );
};

const ICONS = { FlaskConical, FileCheck2, Network };

const Dot = ({ type }) => {
  if (type === "f")
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" className="mx-auto">
        <circle cx="10" cy="10" r="8" fill="#F0A322" />
      </svg>
    );
  if (type === "p")
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" className="mx-auto">
        <circle cx="10" cy="10" r="7.5" fill="none" stroke="#E8930C" strokeWidth="1.5" />
        <path d="M10 2.5 A7.5 7.5 0 0 0 10 17.5 Z" fill="#F0A322" />
      </svg>
    );
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" className="mx-auto">
      <circle cx="10" cy="10" r="7.5" fill="none" stroke="#D9CFB8" strokeWidth="1.5" />
    </svg>
  );
};

const RatingCell = ({ v, isMedhive }) => {
  if (v === "f" || v === "p" || v === "n") return <Dot type={v} />;
  // text values (Low / High)
  if (isMedhive) return <span className="text-sm font-bold text-[#F0A322]">{v}</span>;
  if (v === "Low") return <span className="text-sm font-bold text-emerald-600">{v}</span>;
  return <span className="text-sm font-semibold text-[#37311F]">{v}</span>;
};

const cardCls =
  "rounded-2xl border border-[#EDE5D3] bg-white shadow-[0_2px_16px_rgba(31,27,19,0.04)] transition-all duration-300 hover:border-[#F0A322]/50 hover:shadow-[0_8px_30px_rgba(232,147,12,0.12)]";

const Home = () => {
  return (
    <div className="bg-white text-[#1F1B13]" data-testid="home-page">
      {/* ================= HERO ================= */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden bg-[#FDFBF7] pt-24">
        <HexPattern opacity={0.08} />
        <div
          className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full"
          style={{ background: "radial-gradient(ellipse at center, rgba(240,163,34,0.14), transparent 60%)" }}
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-40"
          style={{ background: "linear-gradient(to top, #FDFBF7, transparent)" }}
        />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-24 lg:px-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Eyebrow>Neuro-Symbolic AI for MedTech</Eyebrow>
            </motion.div>
            <h1 className="mt-7 font-heading text-4xl font-bold uppercase leading-[1.08] tracking-tight text-[#1F1B13] sm:text-6xl lg:text-7xl" data-testid="hero-heading">
              <SplitText text="REVOLUTIONIZING THE" />
              <br />
              <span className="text-[#E8930C]">
                <SplitText text="MEDICAL DEVICE" delay={0.25} />
              </span>
              <br />
              <SplitText text="DEVELOPMENT" delay={0.5} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-7 max-w-2xl text-base leading-relaxed text-[#5D564A] sm:text-lg"
            >
              {HERO.body}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Magnetic>
                <Link
                  to="/contact-us"
                  data-testid="hero-book-demo"
                  className="group inline-flex items-center gap-2.5 rounded-full bg-[#F0A322] px-8 py-4 text-sm font-semibold uppercase tracking-wide text-[#231A07] transition-colors duration-200 hover:bg-[#E8930C]"
                >
                  {HERO.cta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Magnetic>
              <Magnetic strength={0.2}>
                <a
                  href={BOOK_DEMO_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-walkthrough"
                  className="inline-flex items-center gap-2 rounded-full border border-[#D9D0BC] px-8 py-4 text-sm font-semibold uppercase tracking-wide text-[#5D564A] transition-colors duration-200 hover:border-[#E8930C] hover:text-[#B45309]"
                >
                  {HERO.ctaSecondary}
                </a>
              </Magnetic>
            </motion.div>
          </div>
        </div>

        <FloatingHexes />

        {/* Animated MedHive emblem */}
        <div className="pointer-events-none absolute right-[7%] top-[46%] hidden -translate-y-1/2 lg:block" data-testid="hero-emblem">
          <div className="relative flex h-[420px] w-[420px] items-center justify-center">
            {/* soft glow */}
            <motion.div
              animate={{ opacity: [0.5, 0.85, 0.5], scale: [1, 1.05, 1] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute h-72 w-72 rounded-full"
              style={{ background: "radial-gradient(circle, rgba(240,163,34,0.22), transparent 65%)" }}
            />
            {/* rotating hex orbit ring */}
            <motion.svg
              animate={{ rotate: 360 }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              className="absolute"
              width="400"
              height="400"
              viewBox="0 0 400 400"
              fill="none"
            >
              <path
                d="M200 20 L356 110 L356 290 L200 380 L44 290 L44 110 Z"
                stroke="#E8930C"
                strokeOpacity="0.35"
                strokeWidth="1.5"
                strokeDasharray="10 12"
              />
              <circle cx="200" cy="20" r="6" fill="#F0A322" />
              <circle cx="44" cy="290" r="4" fill="#F0A322" fillOpacity="0.7" />
              <circle cx="356" cy="290" r="5" fill="#F0A322" fillOpacity="0.5" />
            </motion.svg>
            {/* counter-rotating inner ring */}
            <motion.svg
              animate={{ rotate: -360 }}
              transition={{ duration: 55, repeat: Infinity, ease: "linear" }}
              className="absolute"
              width="300"
              height="300"
              viewBox="0 0 300 300"
              fill="none"
            >
              <path
                d="M150 18 L264 84 L264 216 L150 282 L36 216 L36 84 Z"
                stroke="#E8930C"
                strokeOpacity="0.2"
                strokeWidth="1"
              />
            </motion.svg>
            {/* floating emblem */}
            <motion.img
              src="/assets/logo-hex-hd.png"
              alt="MedHive emblem"
              className="relative w-44 drop-shadow-[0_16px_40px_rgba(232,147,12,0.35)]"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: [1, 1.04, 1], y: [0, -12, 0] }}
              transition={{
                opacity: { duration: 0.9, delay: 0.4 },
                scale: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.3 },
                y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.3 },
              }}
            />
          </div>
        </div>
      </section>

      {/* ================= MARQUEE STRIP ================= */}
      <section className="border-y border-[#EDE5D3] bg-white py-6" data-testid="marquee-strip">
        <Marquee items={MARQUEE_ITEMS} />
      </section>

      {/* ================= CHANGE IMPACT (primary value block) ================= */}
      <section className="relative overflow-hidden pt-24 pb-16 lg:pt-32 lg:pb-20" id="change-impact">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <FadeIn>
              <Eyebrow>{CHANGE_IMPACT.eyebrow}</Eyebrow>
              <h2 className="mt-6 font-heading text-3xl font-bold leading-tight text-[#1F1B13] sm:text-5xl" data-testid="change-impact-heading">
                <BlurText text="Cross-system change impact for" /> <span className="text-[#E8930C]"><BlurText text="sustaining engineering" delay={0.3} /></span>
              </h2>
              <p className="mt-7 text-base leading-relaxed text-[#5D564A]">{CHANGE_IMPACT.body}</p>
              <Magnetic strength={0.2}>
                <Link
                  to="/contact-us"
                  className="group mt-9 inline-flex items-center gap-2.5 rounded-full bg-[#F0A322] px-8 py-4 text-sm font-semibold uppercase tracking-wide text-[#231A07] shadow-[0_8px_28px_rgba(240,163,34,0.35)] transition-colors duration-200 hover:bg-[#E8930C]"
                >
                  join the design-partner programme
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Magnetic>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="relative" data-testid="change-impact-systems">
                <div
                  className="pointer-events-none absolute inset-0 rounded-full sm:-inset-8"
                  style={{ background: "radial-gradient(ellipse at center, rgba(240,163,34,0.1), transparent 65%)" }}
                />
                <div className="relative grid gap-4 sm:grid-cols-2">
                  {CHANGE_IMPACT.systems.map((s) => {
                    const Icon = CI_ICONS[s.icon];
                    return (
                      <SpotlightCard key={s.label} className={`${cardCls} flex items-center gap-4 p-5`}>
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F0A322]/[0.12] text-[#B45309]">
                          <Icon className="h-5 w-5" />
                        </div>
                        <span className="text-sm font-semibold text-[#37311F]">{s.label}</span>
                      </SpotlightCard>
                    );
                  })}
                </div>
                <p className="relative mt-5 text-center text-xs text-[#8A8272]">
                  One change — every affected artifact, traced in one view.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <section className="relative pt-12 pb-24 lg:pt-16 lg:pb-32" id="about">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid items-start gap-14 lg:grid-cols-2">
            <FadeIn>
              <Eyebrow>{ABOUT.eyebrow}</Eyebrow>
              <h2 className="mt-6 font-heading text-3xl font-bold leading-tight text-[#1F1B13] sm:text-5xl" data-testid="about-heading">
                <BlurText text="Welcome to" /> <span className="text-[#E8930C]"><BlurText text="MEDHIVE" delay={0.2} /></span>
              </h2>
            </FadeIn>
            <FadeIn delay={0.15}>
              <p className="text-base leading-relaxed text-[#5D564A] sm:text-lg">{ABOUT.body}</p>
            </FadeIn>
          </div>

          <div className="mt-16">
            <FadeIn>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#8A8272]" data-testid="targets-label">
                {ABOUT.targetsLabel}
              </p>
            </FadeIn>
            <div className="mt-5 grid gap-5 sm:grid-cols-3" data-testid="stats-band">
              {ABOUT.stats.map((s, i) => (
                <FadeIn key={s.label} delay={i * 0.12} className="h-full">
                  <SpotlightCard className={`h-full ${cardCls} p-8`}>
                    <div className="font-heading text-2xl font-bold text-[#E8930C] lg:text-3xl">{s.value}</div>
                    <p className="mt-3 text-sm font-semibold text-[#37311F]">{s.label}</p>
                    <p className="mt-2 text-xs italic text-[#8A8272]">{ABOUT.targetsCaption}</p>
                  </SpotlightCard>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= PLATFORM ================= */}
      <section className="relative overflow-hidden bg-[#FAF6EE] py-24 lg:py-32" id="platform">
        <HexPattern opacity={0.05} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <FadeIn className="mx-auto max-w-3xl text-center">
            <Eyebrow>{PLATFORM.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-heading text-3xl font-bold text-[#1F1B13] sm:text-5xl" data-testid="platform-heading">
              <BlurText text="How" /> <span className="text-[#E8930C]"><BlurText text="MEDHIVE" delay={0.1} /></span> <BlurText text="Works" delay={0.2} />
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#5D564A] sm:text-lg">{PLATFORM.body}</p>
          </FadeIn>

          <div className="mt-16 grid gap-6 md:grid-cols-3" data-testid="platform-cards">
            {PLATFORM.cards.map((c, i) => {
              const Icon = ICONS[c.icon];
              return (
                <FadeIn key={c.title} delay={i * 0.12} className="h-full">
                  <SpotlightCard className={`group h-full ${cardCls} p-8`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#F0A322]/[0.12] text-[#B45309] transition-colors duration-300 group-hover:bg-[#F0A322]/25">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="mt-6 font-heading text-xl font-semibold text-[#1F1B13]">{c.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#6B6353]">{c.body}</p>
                    <Link
                      to={`/platform#sol${i + 1}`}
                      className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#B45309]"
                    >
                      learn more
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                  </SpotlightCard>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= USE CASES ================= */}
      <section className="relative py-24 lg:py-32" id="use-cases">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <FadeIn>
              <Eyebrow>{USE_CASES.eyebrow}</Eyebrow>
              <h2 className="mt-6 font-heading text-3xl font-bold leading-tight text-[#1F1B13] sm:text-5xl" data-testid="usecases-heading">
                <BlurText text="Making a Big Impact on" /> <span className="text-[#E8930C]"><BlurText text="People's Lives" delay={0.3} /></span>
              </h2>
              <p className="mt-7 text-base leading-relaxed text-[#5D564A]">{USE_CASES.body1}</p>
              <p className="mt-5 text-base leading-relaxed text-[#5D564A]">{USE_CASES.body2}</p>
              <Link
                to="/contact-us"
                className="group mt-9 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#B45309]"
              >
                {USE_CASES.cta}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="relative">
                <div
                  className="pointer-events-none absolute inset-0 rounded-full sm:-inset-8"
                  style={{ background: "radial-gradient(ellipse at center, rgba(240,163,34,0.1), transparent 65%)" }}
                />
                <div className="relative grid gap-5 sm:grid-cols-2">
                  <SpotlightCard className={`${cardCls} p-7 sm:mt-10`}>
                    <HeartPulse className="h-8 w-8 text-[#E8930C]" />
                    <h4 className="mt-4 font-heading text-lg font-semibold text-[#1F1B13]">Advanced Implants</h4>
                    <p className="mt-2 text-sm text-[#6B6353]">From concept to patient bedside faster than ever before.</p>
                  </SpotlightCard>
                  <SpotlightCard className={`${cardCls} p-7`}>
                    <Activity className="h-8 w-8 text-[#E8930C]" />
                    <h4 className="mt-4 font-heading text-lg font-semibold text-[#1F1B13]">Vital Diagnostics</h4>
                    <p className="mt-2 text-sm text-[#6B6353]">Superior product reliability and fewer recalls.</p>
                  </SpotlightCard>
                  <SpotlightCard className={`${cardCls} p-7 sm:mt-10`}>
                    <ShieldCheck className="h-8 w-8 text-[#E8930C]" />
                    <h4 className="mt-4 font-heading text-lg font-semibold text-[#1F1B13]">Enhanced Outcomes</h4>
                    <p className="mt-2 text-sm text-[#6B6353]">Directly improving health outcomes and quality of life.</p>
                  </SpotlightCard>
                  <SpotlightCard className={`${cardCls} p-7`}>
                    <HeartPulse className="h-8 w-8 text-[#E8930C]" />
                    <h4 className="mt-4 font-heading text-lg font-semibold text-[#1F1B13]">Next-Gen Pacemakers</h4>
                    <p className="mt-2 text-sm text-[#6B6353]">Reaching patients years sooner.</p>
                  </SpotlightCard>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ================= SECURITY ================= */}
      <section className="relative overflow-hidden bg-[#FAF6EE] py-24 lg:py-32" id="security">
        <HexPattern opacity={0.04} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <FadeIn className="order-2 lg:order-1">
              <div className="relative mx-auto flex max-w-md items-center justify-center py-10">
                <motion.div
                  animate={{ scale: [1, 1.06, 1], opacity: [0.6, 0.9, 0.6] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute h-72 w-72 rounded-full border border-[#E8930C]/30"
                />
                <motion.div
                  animate={{ scale: [1.06, 1, 1.06], opacity: [0.35, 0.65, 0.35] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute h-96 w-96 rounded-full border border-[#E8930C]/15"
                />
                <div className="relative flex h-44 w-44 items-center justify-center rounded-full bg-white shadow-[0_8px_40px_rgba(232,147,12,0.18)]">
                  <ShieldCheck className="h-20 w-20 text-[#E8930C]" />
                </div>
              </div>
            </FadeIn>
            <div className="order-1 lg:order-2">
              <FadeIn>
                <Eyebrow>{SECURITY.eyebrow}</Eyebrow>
                <h2 className="mt-6 font-heading text-3xl font-bold text-[#1F1B13] sm:text-5xl" data-testid="security-heading">
                  <BlurText text="Committed to" /> <span className="text-[#E8930C]"><BlurText text="Security" delay={0.2} /></span>
                </h2>
                <p className="mt-6 text-base leading-relaxed text-[#5D564A]">{SECURITY.body}</p>
              </FadeIn>
              <ul className="mt-9 space-y-4">
                {SECURITY.points.map((p, i) => (
                  <FadeIn key={p} delay={i * 0.08}>
                    <li className="flex items-start gap-3 rounded-xl border border-[#EDE5D3] bg-white px-5 py-4 shadow-[0_2px_10px_rgba(31,27,19,0.03)] transition-colors duration-300 hover:border-[#F0A322]/50">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#E8930C]" />
                      <span className="text-sm text-[#4A4436]">{p}</span>
                    </li>
                  </FadeIn>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHY US / COMPARISON ================= */}
      <section className="relative py-24 lg:py-32" id="why-us">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeIn className="mx-auto max-w-3xl text-center">
            <Eyebrow>{WHY_US.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-heading text-3xl font-bold text-[#1F1B13] sm:text-5xl" data-testid="whyus-heading">
              <BlurText text="Why" /> <span className="text-[#E8930C]"><BlurText text="MEDHIVE" delay={0.1} /></span><BlurText text="?" delay={0.2} />
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#5D564A]">{WHY_US.body}</p>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="mt-16 overflow-x-auto rounded-2xl border border-[#EDE5D3] shadow-[0_2px_16px_rgba(31,27,19,0.04)]" data-testid="comparison-table">
              <table className="w-full min-w-[1080px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-[#EDE5D3] bg-[#FAF6EE]">
                    <th className="px-6 py-5 text-sm font-semibold uppercase tracking-wider text-[#5D564A]">Feature</th>
                    {WHY_US.competitors.map((c, i) => (
                      <th
                        key={c}
                        className={`px-4 py-5 text-center text-sm font-semibold ${
                          i === 0 ? "bg-[#26211A] text-[#F0A322]" : "text-[#5D564A]"
                        }`}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {WHY_US.matrix.map((row) => (
                    <tr key={row.feature} className="border-b border-[#F2EDE1] transition-colors duration-200 hover:bg-[#FDFBF7]">
                      <td className="px-6 py-4 text-sm font-medium text-[#37311F]">{row.feature}</td>
                      {row.vals.map((v, i) => (
                        <td key={i} className={`px-4 py-4 text-center ${i === 0 ? "bg-[#26211A]" : ""}`}>
                          <RatingCell v={v} isMedhive={i === 0} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs text-[#8A8272]">
              <span className="flex items-center gap-1.5">
                <Dot type="f" /> Full support
              </span>
              <span className="flex items-center gap-1.5">
                <Dot type="p" /> Partial
              </span>
              <span className="flex items-center gap-1.5">
                <Dot type="n" /> Not supported
              </span>
            </div>
            <div className="mx-auto mt-6 max-w-4xl space-y-1.5 text-center">
              {WHY_US.footnotes.map((note) => (
                <p key={note} className="text-xs italic leading-relaxed text-[#8A8272]">
                  {note}
                </p>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ================= HOME LEAD FORM ================= */}
      <section className="relative border-t border-[#F2EDE1] py-24 lg:py-28" id="contact">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <FadeIn>
              <Eyebrow>get in touch</Eyebrow>
              <h2 className="mt-6 font-heading text-3xl font-bold leading-tight text-[#1F1B13] sm:text-5xl" data-testid="home-lead-heading">
                <BlurText text="Bring us a" /> <span className="text-[#E8930C]"><BlurText text="real change scenario" delay={0.2} /></span>
              </h2>
              <p className="mt-6 text-base leading-relaxed text-[#5D564A]">
                Tell us about a change your team recently traced by hand — we'll show you what MedHive would have surfaced, and we will measure the difference with you.
              </p>
            </FadeIn>
            <FadeIn delay={0.15}>
              <HomeLeadForm />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ================= CTA BAND ================= */}
      <section className="relative overflow-hidden bg-[#FAF6EE]">
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(240,163,34,0.16) 0%, rgba(240,163,34,0.04) 55%, transparent 100%)" }}
        />
        <HexPattern opacity={0.06} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-[#1F1B13] sm:text-4xl">
                Designed to cut change-package and <span className="text-[#E8930C]">documentation time</span>
              </h2>
              <p className="mt-3 max-w-xl text-[#5D564A]">
                We will measure it with you — join the design-partner programme.
              </p>
            </div>
            <Magnetic>
              <Link
                to="/contact-us"
                data-testid="cta-book-demo"
                className="group inline-flex items-center gap-2.5 rounded-full bg-[#F0A322] px-8 py-4 text-sm font-semibold uppercase tracking-wide text-[#231A07] transition-colors duration-200 hover:bg-[#E8930C]"
              >
                join the design-partner programme
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </Magnetic>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
